import React, { useState, useContext, useEffect } from 'react';
import { Link} from 'react-router-dom';
import { MessageSquare, Plus, Users, Clock, MessageCircle, ArrowLeft, Send, Search, Filter, ChevronUp, ChevronDown } from 'react-feather';
import { AuthContext } from '../AuthContext';
import { getForumTopics, getForumCategories } from '../../MongoDb/GetForum';
import { url as API_BASE_URL } from "../../MongoDb/MongoConstants";
import './Forum.css';
import '../shared/PageHeader.css';

const Forum = () => {
  const { user, userProfile } = useContext(AuthContext);
  const [activeView, setActiveView] = useState('topics'); // 'topics' | 'thread'
  const [selectedThread, setSelectedThread] = useState(null);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [newMessage, setNewMessage] = useState('');
  const [isPostingReply, setIsPostingReply] = useState(false);
  const [newTopic, setNewTopic] = useState({
    title: '',
    category: 'general',
    content: '',
    tags: [],
    tagsInput: ''
  });
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);
  const [createTopicError, setCreateTopicError] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [userVote, setUserVote] = useState(null); // 'upvote' | 'downvote' | null
  
  // Data states
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([
    { id: 'all', name: 'All Topics', color: '#3b82f6' },
    { id: 'general', name: 'General', color: '#6b7280' },
    { id: 'nba', name: 'NBA', color: '#f59e0b' },
    { id: 'nfl', name: 'NFL', color: '#10b981' },
    { id: 'models', name: 'Models', color: '#8b5cf6' },
    { id: 'feedback', name: 'Feedback', color: '#ef4444' }
  ]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [topicsError, setTopicsError] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load topics when component mounts or user changes
  useEffect(() => {
    if (user) {
      loadTopics();
    }
  }, [user]);

  const profile = userProfile;

  const loadTopics = async () => {
    setIsLoadingTopics(true);
    setTopicsError(null);
    
    try {
      const response = await getForumTopics({
        category: 'all',
        search: '',
        limit: 50,
        sort: 'newest'
      });
      
      if (response.success) {
        setTopics(response.data.topics || []);
      } else {
        setTopicsError(response.error || 'Failed to load topics');
        console.error('Failed to load topics:', response.error);
      }
    } catch (error) {
      setTopicsError('Network error occurred');
      console.error('Error loading topics:', error);
    } finally {
      setIsLoadingTopics(false);
    }
  };

  const loadCategories = async () => {
    // Categories are now hardcoded above in the state initialization
    // No need to fetch from server
  };

  // Filter topics based on selected category and search query
  const filteredTopics = topics.filter(topic => {
    // Category filter
    const matchesCategory = filterCategory === 'all' || 
                           topic.category?.toLowerCase() === filterCategory.toLowerCase();
    
    // Search filter  
    const matchesSearch = !debouncedSearchQuery || 
                         topic.title?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
                         topic.content?.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleCreateTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.content.trim()) {
      setCreateTopicError('Title and content are required');
      return;
    }
    
    if (!user) {
      setCreateTopicError('You must be logged in to create a topic');
      return;
    }
    
    setIsCreatingTopic(true);
    setCreateTopicError(null);
    
    try {
      // Call the API to create the topic
      const response = await fetch(`${API_BASE_URL}/api/forum/topic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTopic.title,
          content: newTopic.content,
          category: newTopic.category,
          authorId: user.id,
          authorName: profile?.name || user.email || 'Anonymous',
          authorAvatar: profile?.avatar || null,
          tags: newTopic.tags || []
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Topic created successfully:', result.topic);
        
        // Reset form and close modal
        setNewTopic({ 
          title: '', 
          category: 'general', 
          content: '',
          tags: [],
          tagsInput: ''
        });
        setShowNewTopicModal(false);
        
        // Refresh the topics list to include the new topic
        await loadTopics();
      } else {
        const errorData = await response.json();
        setCreateTopicError(errorData.error || 'Failed to create topic');
      }
      
    } catch (error) {
      console.error('Error creating topic:', error);
      setCreateTopicError('Network error. Please try again.');
    } finally {
      setIsCreatingTopic(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    if (!user || !selectedThread) {
      console.error('User not logged in or no thread selected');
      return;
    }
    
    setIsPostingReply(true);
    
    try {
      // Call the API to post the reply
      const response = await fetch(`${API_BASE_URL}/api/forum/topic/${selectedThread.id || selectedThread._id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: newMessage.trim(),
          authorId: user.id,
          authorName: profile?.name || user.email || 'Anonymous',
          authorAvatar: profile?.avatar || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Reply posted successfully:', result.reply);
        
        // Clear the input
        setNewMessage('');
        
        // Add the new reply to the selected thread immediately
        const newReply = {
          id: result.reply.id,
          _id: result.reply._id,
          content: result.reply.content,
          author: {
            id: user.id,
            name: profile?.name || user.email || 'Anonymous',
            avatar: profile?.avatar || null
          },
          upvotes: 0,
          downvotes: 0,
          netVotes: 0,
          voters: [],
          isEdited: false,
          isBestAnswer: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Update the selected thread with new reply and incremented count
        setSelectedThread(prev => ({
          ...prev,
          repliesCount: (prev.repliesCount || 0) + 1,
          replies: [...(prev.replies || []), newReply]
        }));
        
        // Refresh the topics list to update reply counts in the topics view
        await loadTopics();
      } else {
        const errorData = await response.json();
        console.error('Failed to post reply:', errorData.error);
      }
      
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setIsPostingReply(false);
    }
  };

  const handleVote = async (voteType) => {
    if (!user || !selectedThread) {
      console.error('User not logged in or no thread selected');
      return;
    }
    
    setIsVoting(true);
    
    try {
      // If user clicks the same vote type they already have, remove the vote
      const actualVoteType = userVote === voteType ? 'remove' : voteType;
      
      const response = await fetch(`${API_BASE_URL}/api/forum/topic/${selectedThread.id || selectedThread._id}/vote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userName: profile?.name || user.email || 'Anonymous',
          voteType: actualVoteType
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Update the selected thread with new vote counts
        setSelectedThread(prev => ({
          ...prev,
          upvotes: result.topic.upvotes,
          downvotes: result.topic.downvotes,
          netVotes: result.topic.netVotes
        }));
        
        // Update user's current vote state
        setUserVote(result.userVote);
        
        console.log(result.message);
      } else {
        const errorData = await response.json();
        console.error('Vote failed:', errorData.error);
      }
      
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const handleReplyVote = async (replyId, voteType) => {
    if (!user || !selectedThread) {
      console.error('User not logged in or no thread selected');
      return;
    }
    
    try {
      // Find the current reply to check existing vote
      const reply = selectedThread.replies?.find(r => 
        (r._id && r._id.toString() === replyId) || r.id === replyId
      );
      
      if (!reply) {
        console.error('Reply not found');
        return;
      }
      
      // Check if user has already voted on this reply
      const existingVote = reply.voters?.find(voter => voter.userId === user.id);
      const actualVoteType = existingVote?.voteType === voteType ? 'remove' : voteType;
      
      const response = await fetch(`${API_BASE_URL}/api/forum/topic/${selectedThread._id || selectedThread.id}/reply/${replyId}/vote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userName: profile?.name || user.email || 'Anonymous',
          voteType: actualVoteType
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Update the selected thread with the updated topic data
        const sanitizedTopic = {
          ...result.topic,
          id: result.topic._id?.toString() || result.topic.id,
          replies: result.topic.replies?.map(reply => ({
            ...reply,
            id: reply._id?.toString() || reply.id,
            _id: reply._id?.toString() || reply.id
          })) || []
        };
        
        setSelectedThread(sanitizedTopic);
        console.log(result.message);
      } else {
        const errorData = await response.json();
        console.error('Reply vote failed:', errorData.error);
      }
      
    } catch (error) {
      console.error('Error voting on reply:', error);
    }
  };

  const openThread = async (topic) => {
    setSelectedThread(topic);
    setActiveView('thread');
    
    // Refresh the topic data to get the latest replies
    try {
      const response = await getForumTopics({
        category: 'all',
        search: '',
        limit: 1,
        sort: 'newest',
        topicId: topic.id
      });
      
      if (response.success && response.data.topics.length > 0) {
        const updatedTopic = response.data.topics.find(t => t.id === topic.id);
        if (updatedTopic) {
          setSelectedThread(updatedTopic);
        }
      }
    } catch (error) {
      console.error('Error refreshing topic data:', error);
    }
    
    // Check if user has already voted on this topic
    if (user && topic.voters) {
      const userVoteRecord = topic.voters.find(v => v.userId === user.id);
      setUserVote(userVoteRecord ? userVoteRecord.vote : null);
    } else {
      setUserVote(null);
    }
  };

  const backToTopics = () => {
    setActiveView('topics');
    setSelectedThread(null);
  };

  if (!user) {
    return (
      <div className="forum-container">
        <div className="forum-login-prompt">
          <div className="forum-login-content">
            <MessageSquare className="forum-login-icon" />
            <h2>Join the Discussion</h2>
            <p>Connect with other members</p>
            <p className="login-message">Please <Link to="/login">log in</Link> to access the Forum</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forum-container">
      {/* Forum Header */}
      <div className="top-header-header">
        <div className="top-header-title">
          <MessageSquare className="top-header-icon" />
          <div>
            <h1>Community Forum</h1>
          </div>
          <span className="top-header-beta">BETA</span>
        </div>
        <p className="top-header-subtitle">
          {activeView === 'topics' 
            ? 'Connect, discuss, and share insights with the community'
            : selectedThread?.title
          }
        </p>
      </div>

      {activeView === 'topics' ? (
        <>
          {/* Controls */}
          <div className="forum-controls">
            <div className="forum-search">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div className="forum-filters">
              <Filter className="filter-icon" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <button 
              className="new-topic-btn"
              onClick={() => setShowNewTopicModal(true)}
            >
              <Plus size={18} />
              New Topic
            </button>
          </div>

          {/* Categories Overview */}
          <div className="categories-grid">
            {/* All Topics Card */}
            <div 
              className={`category-card ${filterCategory === 'all' ? 'active' : ''}`}
              onClick={() => setFilterCategory('all')}
              style={{ cursor: 'pointer' }}
            >
              <div 
                className="category-color" 
                style={{ backgroundColor: '#3b82f6' }}
              ></div>
              <div className="category-info">
                <h3>All Topics</h3>
                <p>{topics.length} topics</p>
              </div>
            </div>
            
            {/* Individual Category Cards */}
            {categories.filter(cat => cat.id !== 'all').map(category => (
              <div 
                key={category.id} 
                className={`category-card ${filterCategory === category.id ? 'active' : ''}`}
                onClick={() => setFilterCategory(category.id)}
                style={{ cursor: 'pointer' }}
              >
                <div 
                  className="category-color" 
                  style={{ backgroundColor: category.color }}
                ></div>
                <div className="category-info">
                  <h3>{category.name}</h3>
                  <p>{topics.filter(t => t.category?.toLowerCase() === category.id).length} topics</p>
                </div>
              </div>
            ))}
          </div>

          {/* Topics List */}
          <div className="topics-section">
            <div className="topics-header">
              <h2>Recent Topics</h2>
              <span className="topics-count">
                {isLoadingTopics ? 'Loading...' : `${filteredTopics.length} topics`}
              </span>
            </div>
            
            <div className="topics-list">
              {isLoadingTopics ? (
                <div className="loading-topics">
                  <div className="loading-spinner"></div>
                  <p>Loading topics...</p>
                </div>
              ) : topicsError ? (
                <div className="error-message">
                  <p>Error loading topics: {topicsError}</p>
                  <button className="retry-btn" onClick={loadTopics}>
                    Retry
                  </button>
                </div>
              ) : filteredTopics.length === 0 ? (
                <div className="no-topics">
                  <MessageSquare size={48} />
                  <h3>No topics found</h3>
                  <p>
                    {searchQuery || filterCategory !== 'all' 
                      ? 'Try adjusting your search or filter criteria'
                      : 'Be the first to start a discussion!'
                    }
                  </p>
                </div>
              ) : (
                filteredTopics.map(topic => (
                  <div 
                    key={topic.id} 
                    className={`topic-item ${topic.isPinned ? 'pinned' : ''}`}
                    onClick={() => openThread(topic)}
                  >
                    <div className="topic-main">
                      <div className="topic-avatar">
                        {topic.authorAvatar || 
                         (typeof topic.author === 'string' 
                           ? topic.author.substring(0, 2).toUpperCase()
                           : topic.author?.name?.substring(0, 2).toUpperCase() || 'AU')}
                      </div>
                      <div className="topic-content">
                        <div className="topic-header-row">
                          <h3 className="topic-title">
                            {topic.isPinned && <span className="pin-indicator">📌</span>}
                            {topic.title}
                          </h3>
                          <span 
                            className="topic-category"
                            style={{ 
                              backgroundColor: categories.find(c => c.id === topic.category.toLowerCase())?.color || '#6b7280'
                            }}
                          >
                            {topic.category}
                          </span>
                        </div>
                        <div className="topic-meta">
                          <span>by {typeof topic.author === 'string' ? topic.author : topic.author?.name || 'Anonymous'}</span>
                          <span>•</span>
                          <span>
                            Created: {topic.createdAt ? 
                              new Date(topic.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'No activity'
                            }
                          </span>
                          <span>•</span>
                          <span>
                            Updated: {topic.lastActivity ? 
                              new Date(topic.lastActivity).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'No activity'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="topic-stats">
                      <div className="stat-item">
                        <MessageCircle size={16} />
                        <span>{topic.repliesCount}</span>
                      </div>
                      <div className="stat-item">
                        <Users size={16} />
                        <span>{topic.views}</span>
                      </div>
                      <div className="stat-item vote-stat">
                        <span className={`vote-count ${(topic.netVotes || 0) >= 0 ? 'positive' : 'negative'}`}>
                          {(topic.netVotes || 0) > 0 ? '+' : ''}{topic.netVotes || 0}
                        </span>
                      </div>
                      <div className="last-reply">
                        <span>Last by {topic.author?.name || 'Unknown'}</span>
                        <span>
                          {topic.updatedAt ? 
                            new Date(topic.updatedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'No activity'
                          }
                      </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : (
        /* Thread View */
        <div className="thread-view">
          <div className="thread-header">
            <button className="back-btn" onClick={backToTopics}>
              <ArrowLeft size={18} />
              Back to Topics
            </button>
            <div className="thread-info">
              <h2>{selectedThread?.title}</h2>
              <div className="thread-meta">
                <span>by {typeof selectedThread?.author === 'string' ? selectedThread.author : selectedThread?.author?.name || 'Anonymous'}</span>
                <span>•</span>
                <span>{selectedThread?.repliesCount} replies</span>
                <span>•</span>
                <span>{selectedThread?.views} views</span>
              </div>
              
              {/* Voting controls */}
              <div className="vote-controls">
                <button 
                  className={`vote-button upvote ${userVote === 'upvote' ? 'active' : ''}`}
                  onClick={() => handleVote('upvote')}
                  disabled={isVoting}
                  title="Upvote this topic"
                >
                  ▲ {selectedThread?.upvotes || 0}
                </button>
                
                <span className="net-votes">
                  {(selectedThread?.netVotes || 0) > 0 ? '+' : ''}{selectedThread?.netVotes || 0}
                </span>
                
                <button 
                  className={`vote-button downvote ${userVote === 'downvote' ? 'active' : ''}`}
                  onClick={() => handleVote('downvote')}
                  disabled={isVoting}
                  title="Downvote this topic"
                >
                  ▼ {selectedThread?.downvotes || 0}
                </button>
              </div>
            </div>
          </div>

          <div className="messages-container">
            {/* Original Topic Post */}
            <div className="message-item original-post">
              <div className="message-avatar">
                {selectedThread?.authorAvatar || 
                 (typeof selectedThread?.author === 'string' 
                   ? selectedThread.author.substring(0, 2).toUpperCase()
                   : selectedThread?.author?.name?.substring(0, 2).toUpperCase() || 'AU')}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-author">
                    {typeof selectedThread?.author === 'string' 
                      ? selectedThread.author 
                      : selectedThread?.author?.name || 'Anonymous'}
                  </span>
                  <span className="message-time">
                    {new Date(selectedThread?.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="message-text">{selectedThread?.content}</div>
              </div>
            </div>

            {/* Replies */}
            {Array.isArray(selectedThread?.replies) && selectedThread.replies.length > 0 ? (
              selectedThread.replies.map(reply => {
                const userReplyVote = reply.voters?.find(voter => 
                  voter.userId === user?.id
                )?.voteType || null;

                return (
                  <div key={reply._id?.toString() || reply.id?.toString() || Math.random().toString()} className="message-item">
                    <div className="message-avatar">
                      {reply.author?.avatar || reply.author?.name?.substring(0, 2).toUpperCase() || 'AN'}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <span className="message-author">{reply.author?.name || 'Anonymous'}</span>
                        <span className="message-time">
                          {new Date(reply.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {reply.isEdited && <span className="edited-indicator">(edited)</span>}
                        {reply.isBestAnswer && <span className="best-answer-badge">✓ Best Answer</span>}
                      </div>
                      <div className="message-text">{reply.content}</div>
                      
                      {/* Reply voting and actions */}
                      <div className="reply-actions">
                        <div className="reply-voting">
                          <button
                            className={`vote-btn upvote ${userReplyVote === 'upvote' ? 'active' : ''}`}
                            onClick={() => handleReplyVote(reply._id?.toString() || reply.id?.toString(), 'upvote')}
                            disabled={!user}
                            title="Upvote this reply"
                          >
                            <ChevronUp size={16} />
                            <span>{reply.upvotes || 0}</span>
                          </button>
                          
                          {(reply.netVotes !== undefined && reply.netVotes !== 0) && (
                            <span className={`vote-count ${reply.netVotes >= 0 ? 'positive' : 'negative'}`}>
                              {reply.netVotes > 0 ? '+' : ''}{reply.netVotes}
                            </span>
                          )}
                          
                          <button
                            className={`vote-btn downvote ${userReplyVote === 'downvote' ? 'active' : ''}`}
                            onClick={() => handleReplyVote(reply._id?.toString() || reply.id?.toString(), 'downvote')}
                            disabled={!user}
                            title="Downvote this reply"
                          >
                            <ChevronDown size={16} />
                            <span>{reply.downvotes || 0}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-replies">
                <p>No replies yet. Be the first to reply!</p>
              </div>
            )}
          </div>

          <div className="reply-section">
            <div className="reply-input-container">
              <div className="reply-avatar">{profile?.name?.substring(0, 2).toUpperCase() || 'U'}</div>
              <div className="reply-input-wrapper">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Write your reply..."
                  className="reply-input"
                  rows={3}
                />
                <button 
                  className="send-btn"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isPostingReply}
                >
                  <Send size={18} />
                  {isPostingReply ? 'Posting...' : 'Reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Topic Modal */}
      {showNewTopicModal && (
        <div className="modal-overlay" onClick={() => setShowNewTopicModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Topic</h2>
              <button 
                className="modal-close"
                onClick={() => setShowNewTopicModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Topic Title</label>
                <input
                  type="text"
                  value={newTopic.title}
                  onChange={(e) => setNewTopic({...newTopic, title: e.target.value})}
                  placeholder="Enter topic title..."
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newTopic.category}
                  onChange={(e) => setNewTopic({...newTopic, category: e.target.value})}
                  className="form-select"
                >
                  {categories.filter(cat => cat.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={newTopic.content}
                  onChange={(e) => setNewTopic({...newTopic, content: e.target.value})}
                  placeholder="What would you like to discuss?"
                  className="form-textarea"
                  rows={6}
                />
              </div>
              
              <div className="form-group">
                <label>Tags (optional)</label>
                <input
                  type="text"
                  value={newTopic.tagsInput || newTopic.tags?.join(', ') || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    setNewTopic({
                      ...newTopic, 
                      tagsInput: inputValue,
                      tags: inputValue.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                    });
                  }}
                  placeholder="Add tags separated by commas (e.g., NBA, strategy, models)"
                  className="form-input"
                />
                <small className="form-help">Add relevant tags to help others find your topic</small>
              </div>
              
              {createTopicError && (
                <div className="error-message">
                  {createTopicError}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowNewTopicModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleCreateTopic}
                disabled={!newTopic.title.trim() || !newTopic.content.trim() || isCreatingTopic}
              >
                {isCreatingTopic ? 'Creating...' : 'Create Topic'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Forum;